import DrawerView from '@app/src/components/Drawer/DrawerView'
import TextField from '@app/src/components/Ui/TextField'
import type { EntitlementFeature, OrganizationEntitlement } from '@app/src/types/entitlements'
import { EntitlementEffect } from '@app/src/types/entitlements'
import { capitalize } from '@app/src/utils/helpers'
import { Alert, CircularProgress, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import React, { useState } from 'react'
import type { AccessCell } from './accessMatrix'
import { customerSees, featureLabel, findRule, isDecidedByFeatureRule, policyLabel, toCell } from './accessMatrix'
import EntitlementStatusChip from './EntitlementStatusChip'
import { useOrganizationEntitlements } from './useOrganizationEntitlements'

const DefaultChoice = 'Default'
type Choice = EntitlementEffect | typeof DefaultChoice

const toChoice = (value: string): Choice =>
  Object.values(EntitlementEffect).find(effect => effect === value) ?? DefaultChoice

type Props = {
  organizationId: number
  organizationName: string
  solutionType: string
  feature: EntitlementFeature
  scopeId: number
  closeDrawer: () => void
}

const EditEntitlementDrawer: React.FC<Props> = ({
  organizationId,
  organizationName,
  solutionType,
  feature,
  scopeId,
  closeDrawer,
}) => {
  const { overview, isLoading, save, isSaving } = useOrganizationEntitlements(organizationId, solutionType)
  const scope = overview?.scopes.find(candidate => candidate.feature === feature && candidate.scopeId === scopeId)

  if (isLoading) return <CircularProgress />

  if (!overview || !scope)
    return (
      <Typography color="error" p={3}>
        Couldn&apos;t load access for this template.
      </Typography>
    )

  const ownRule = findRule(overview.entitlements, feature, scopeId)

  return (
    <EntitlementForm
      cell={toCell(scope, overview.entitlements)}
      ownRule={ownRule}
      featureRule={findRule(overview.entitlements, feature)}
      organizationName={organizationName}
      isSaving={isSaving}
      onSave={(effect, note) =>
        save(
          [{ feature, scopeId, effect, note, validUntil: effect ? (ownRule?.validUntil ?? null) : null }],
          'Access updated',
          closeDrawer,
        )
      }
    />
  )
}

type FormProps = {
  cell: AccessCell
  ownRule?: OrganizationEntitlement
  featureRule?: OrganizationEntitlement
  organizationName: string
  isSaving: boolean
  onSave: (effect: EntitlementEffect | null, note: string | null) => void
}

const EntitlementForm: React.FC<FormProps> = ({ cell, ownRule, featureRule, organizationName, isSaving, onSave }) => {
  const [choice, setChoice] = useState<Choice>(ownRule?.effect ?? DefaultChoice)
  const [note, setNote] = useState(ownRule?.note ?? '')
  const feature = featureLabel[cell.scope.feature]
  const isUsingDefault = choice === DefaultChoice
  const grantOverridesDenyAll = featureRule?.effect === EntitlementEffect.Deny && choice === EntitlementEffect.Grant

  return (
    <DrawerView
      title={cell.scope.title}
      subTitle={`${capitalize(feature)} · ${organizationName}`}
      buttons={[
        {
          label: 'Save',
          variant: 'contained',
          loading: isSaving,
          onClick: () => onSave(isUsingDefault ? null : choice, isUsingDefault || !note.trim() ? null : note.trim()),
        },
      ]}
    >
      <Stack spacing={3} px={3} pb={3}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EntitlementStatusChip status={cell.status} />
            <Typography variant="caption" color="text.secondary">
              {cell.reason}
            </Typography>
          </Stack>
          <Typography variant="body2">The customer sees: {customerSees(cell)}</Typography>
        </Stack>

        {grantOverridesDenyAll ? (
          <Alert severity="warning">“Deny all {feature}” is on, but a grant on this template wins over it.</Alert>
        ) : (
          isDecidedByFeatureRule(cell) && (
            <Alert severity="info">A rule for all {feature} decides this. A rule on this template wins over it.</Alert>
          )
        )}

        <Stack spacing={1}>
          <Typography variant="subtitle1">Access for this organization</Typography>
          <RadioGroup value={choice} onChange={event => setChoice(toChoice(event.target.value))}>
            <FormControlLabel
              value={DefaultChoice}
              control={<Radio />}
              label={`Use template default (${policyLabel[cell.scope.policy]})`}
            />
            <FormControlLabel
              value={EntitlementEffect.Grant}
              control={<Radio />}
              label="Grant: they can use it, even if it is an add-on or hidden"
            />
            <FormControlLabel
              value={EntitlementEffect.Deny}
              control={<Radio />}
              label="Deny: they can't use it, even if it is included for everyone"
            />
          </RadioGroup>
        </Stack>

        <TextField
          label="Note (why, contract, ticket)"
          value={note}
          onChange={event => setNote(event.target.value)}
          disabled={isUsingDefault}
          inputProps={{ maxLength: 500 }}
          multiline
          minRows={2}
          fullWidth
        />
        <Typography variant="caption" color="text.secondary">
          The rule applies to every solution this organization has.
        </Typography>
      </Stack>
    </DrawerView>
  )
}

export default EditEntitlementDrawer
